import React, { useEffect, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import LineChart from "../../../../Components/GraphNCharts/LineChart";
import { get } from "../../../../utils/api";
import {
  generateBarChartDataSetForWaste,
  manipulateDataForBarChart,
} from "../../../../utils/graphFunctions";
import EchartDoughnutChart from "../../../../Components/GraphNCharts/EchartDoughnut";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import {
  generateMonthArray,
  getMonthCount,
} from "../../../../utils/formatDate";
import {
  fixedTons,
  formatNumberCommaSeparated,
  truncateNumber,
} from "../../../../utils/common";
import { Box, Typography } from "@mui/material";
import NoDataFound from "../../../../Components/Masters/NoDataFound";
import useAuthStore from "../../../../store/ContextUser";

const Dashboard = () => {
  const currentDate = new Date();
  const {selectedCompany}=useAuthStore();
  const currentYear = currentDate.getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({
    month: 1,
    year: currentYear,
  });
  const [toDate, setToDate] = useState({
    month: currentDate.getMonth() + 1,
    year: currentYear,
  });
  const [graphView, setGraphView] = useState({
    temporal: "Annual",
  });

  const [dashboardData, setDashboardData] = useState({
    emission: null,
    categoryAnalysis: null,
    vendorAnalysis: null,
    temporalAnalysis: null,
  });

  // TODO: Update these API endpoints when backend dashboard endpoints are available
  const apiurls = {
    emission: `/dashboard/scope3/processing-of-sold-products/emission?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    categoryAnalysis: `/dashboard/scope3/processing-of-sold-products/category?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    vendorAnalysis: `/dashboard/scope3/processing-of-sold-products/vendor?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
    temporalAnalysis: `/dashboard/scope3/processing-of-sold-products/temporal?startYear=${
      fromDate.year
    }&startMonth=${fromDate.month}&endYear=${toDate.year}&endMonth=${
      toDate.month
    }&facilities=${selectedFacility.join(",")}&companyId=${selectedCompany?._id}`,
  };

  const fetchData = async () => {
    try {
      // TODO: Uncomment when backend endpoints are ready
      // const [
      //   emission,
      //   categoryAnalysis,
      //   vendorAnalysis,
      //   temporalAnalysis,
      // ] = await Promise.all([
      //   get(apiurls.emission),
      //   get(apiurls.categoryAnalysis),
      //   get(apiurls.vendorAnalysis),
      //   get(apiurls.temporalAnalysis),
      // ]);
      // setDashboardData({
      //   emission: emission.data,
      //   categoryAnalysis: categoryAnalysis.data,
      //   vendorAnalysis: vendorAnalysis.data,
      //   temporalAnalysis: temporalAnalysis.data,
      // });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedFacility, selectedCompany]);

  const generateYearList = (startYear = 2020) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = startYear; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };
  const yearList = generateYearList(2020);

  const handleGraphChange = (e, type) => {
    if (type === "temporal") {
      setGraphView((prev) => ({ ...prev, temporal: e.target.value }));
    }
  };

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>Processing of Sold Products</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Emission Analysis</p>
        <div className={styles.card_container}>
          <p className={styles.graph_heading}>
            Total Emissions from Processing of Sold Products (tCO2e)
          </p>
          <p className={styles.card_value}>
            {formatNumberCommaSeparated(
              fixedTons(dashboardData?.emission?.totalEmission ?? 0)
            )}
          </p>
        </div>
        <div className={styles.chart_container}>
          <div className={styles.graph_container_header}>
            <div>
              <p className={styles.graph_heading}>
                Emissions by Product Category
              </p>
            </div>
          </div>
          {/* TODO: Add chart when backend data is available */}
          <NoDataFound mt={"20%"} />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Processing Analysis</p>
        <div className={styles.emission_container}>
          <div className={styles.card_container}>
            <div className={styles.graph_container_header}>
              <p className={styles.graph_heading}>Total Processings</p>
              <p className={styles.graph_value}>
                {formatNumberCommaSeparated(
                  dashboardData?.categoryAnalysis?.totalProcessings ?? 0
                )}
              </p>
            </div>
            {/* TODO: Add chart when backend data is available */}
            <NoDataFound mt={"30%"} />
          </div>
          <div className={styles.card_container}>
            <div className={styles.graph_container_header}>
              <p className={styles.graph_heading}>
                Top Product Categories
              </p>
            </div>
            {/* TODO: Add chart when backend data is available */}
            <NoDataFound mt={"30%"} />
          </div>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Vendor Analysis</p>
        <div className={styles.chart_container}>
          <div className={styles.graph_container_header}>
            <p className={styles.graph_heading}>
              Distribution by Vendor
            </p>
          </div>
          {/* TODO: Add chart when backend data is available */}
          <NoDataFound mt={"20%"} />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <p className={styles.sub_heading}>Temporal Analysis</p>
        <div className={styles.chart_container}>
          <div className={styles.temporal_div}>
            <p className={styles.graph_heading}>Year-over-Year Comparison</p>
            <div>
              <select
                name="month"
                id="month"
                value={graphView.temporal}
                onChange={(e) => handleGraphChange(e, "temporal")}
                className={styles.heading_select_month}
              >
                {["Annual", "Year On Year"].map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* TODO: Add chart when backend data is available */}
          <NoDataFound mt={"20%"} />
        </div>
      </div>
    </>
  );
};

export default Dashboard;
